import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Alert,
    Autocomplete,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    CircularProgress,
    Container,
    Drawer,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    LinearProgress,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Paper,
    Select,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Tabs,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import {
    AccessTime as AccessTimeIcon,
    Analytics as AnalyticsIcon,
    AudioFile as AudioIcon,
    CheckCircleOutline as CheckCircleOutlineIcon,
    Close as CloseIcon,
    Collections as BundleIcon,
    Description as WordIcon,
    DeviceHub as DeviceHubIcon,
    Devices as DevicesIcon,
    Download as DownloadIcon,
    FilterAlt as FilterAltIcon,
    Flag as FlagIcon,
    Inbox as InboxIcon,
    Insights as InsightsIcon,
    People as PeopleIcon,
    PictureAsPdf as PdfIcon,
    PlayCircleFilled as PlayIcon,
    Public as PublicIcon,
    Refresh as RefreshIcon,
    RestartAlt as ResetIcon,
    Search as SearchIcon,
    Share as ShareIcon,
    Speed as SpeedIcon,
    ThumbUp as ThumbUpIcon,
    TrendingUp as TrendingUpIcon,
    VideoFile as VideoIcon,
    Visibility as VisibilityIcon,
    Web as WebIcon,
} from '@mui/icons-material';
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    FunnelChart,
    Funnel,
    LabelList,
    Legend,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip as RTooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { useAuth } from '../context/AuthContext';
import config from '../config/config';

const CHART_COLORS = ['#1976d2', '#00bfa5', '#ffb300', '#ef5350', '#8e44ad', '#26a69a', '#3949ab', '#ec407a'];
const DEVICE_COLORS = {
    desktop: '#1976d2',
    mobile: '#00bfa5',
    tablet: '#ffb300',
    unknown: '#90a4ae',
};

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Default look-back window. Mirrors {@code DEFAULT_LOOKBACK_DAYS} on the
 * server side so a no-param request from the admin gets the same slice as
 * the unfiltered admin UI render.
 */
const DEFAULT_LOOKBACK_DAYS = 7;

const todayIso = () => new Date().toISOString();
const isoNDaysAgo = (days) => {
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d.toISOString();
};

const buildDefaultFilters = () => ({
    startDate: isoNDaysAgo(DEFAULT_LOOKBACK_DAYS),
    endDate: todayIso(),
    assetType: '',
    iboNumber: '',
    bundleIds: [],
    assetIds: [],
});

const ASSET_TYPE_META = {
    VIDEO: { icon: VideoIcon, color: '#1976d2', label: 'Video' },
    AUDIO: { icon: AudioIcon, color: '#00bfa5', label: 'Audio' },
    PDF: { icon: PdfIcon, color: '#ef5350', label: 'PDF' },
    WORD: { icon: WordIcon, color: '#3949ab', label: 'Word' },
};

const assetMeta = (type) => ASSET_TYPE_META[(type || '').toUpperCase()] || {
    icon: PlayIcon, color: '#90a4ae', label: type || 'Unknown',
};

/** Animated stat card used across the overview grid. */
const KpiCard = ({ icon: Icon, label, value, color, helper }) => {
    const theme = useTheme();
    return (
        <Card
            elevation={0}
            sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                background: `linear-gradient(135deg, ${color}18 0%, ${color}06 100%)`,
                transition: 'transform .18s ease, box-shadow .18s ease',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 10px 26px -12px ${color}66`,
                },
            }}
        >
            <CardContent sx={{ py: 2.25, px: 2.5 }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Avatar sx={{ bgcolor: color, width: 40, height: 40 }}>
                        <Icon fontSize="small" />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.4, textTransform: 'uppercase', fontWeight: 600 }}>
                            {label}
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{value}</Typography>
                        {helper && (
                            <Typography variant="caption" color="text.secondary">{helper}</Typography>
                        )}
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
};

const SectionHeader = ({ icon: Icon, title, action }) => (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1.2}>
            {Icon && <Icon sx={{ color: 'primary.main' }} />}
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
        </Stack>
        {action}
    </Stack>
);

const EmptyState = ({ icon: Icon = InsightsIcon, message = 'No data yet', hint }) => (
    <Stack alignItems="center" justifyContent="center" sx={{ py: 5, color: 'text.disabled' }}>
        <Icon sx={{ fontSize: 48, mb: 1 }} />
        <Typography variant="body2">{message}</Typography>
        {hint && (
            <Typography variant="caption" sx={{ mt: 0.5, maxWidth: 360, textAlign: 'center' }}>
                {hint}
            </Typography>
        )}
    </Stack>
);

const ContentAnalyticsPage = () => {
    const theme = useTheme();
    const { getAccessToken } = useAuth();
    const API_BASE_URL = config.API_BASE_URL;

    const [filters, setFilters] = useState(buildDefaultFilters);
    const [activeTab, setActiveTab] = useState('overview');

    const [overview, setOverview] = useState(null);
    const [topAssets, setTopAssets] = useState([]);
    const [topBundles, setTopBundles] = useState([]);
    const [recentSessions, setRecentSessions] = useState([]);
    const [viewsByDeviceByDate, setViewsByDeviceByDate] = useState({ devices: [], series: [] });
    const [engagementFunnel, setEngagementFunnel] = useState([]);
    const [timeOfDayHeatmap, setTimeOfDayHeatmap] = useState([]);
    const [browserOs, setBrowserOs] = useState({ browser: {}, os: {} });

    const [bundleOptions, setBundleOptions] = useState([]);
    const [assetOptions, setAssetOptions] = useState([]);

    const [selectedAsset, setSelectedAsset] = useState(null);
    const [retentionCurve, setRetentionCurve] = useState([]);
    const [documentPageStats, setDocumentPageStats] = useState([]);
    const [loadingAsset, setLoadingAsset] = useState(false);

    const [iboSearch, setIboSearch] = useState('');
    const [iboStats, setIboStats] = useState(null);
    const [loadingIbo, setLoadingIbo] = useState(false);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lastRefreshed, setLastRefreshed] = useState(null);

    const [topAssetsSort, setTopAssetsSort] = useState({ key: 'totalWatchTimeSeconds', dir: 'desc' });

    const apiHeaders = useCallback(async () => {
        const token = await getAccessToken();
        if (!token) throw new Error('Authentication token not available');
        return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
    }, [getAccessToken]);

    const callApi = useCallback(async (path, { method = 'GET', body } = {}) => {
        const headers = await apiHeaders();
        const res = await fetch(`${API_BASE_URL}${path}`, {
            method,
            headers,
            body: body ? JSON.stringify(body) : undefined,
        });
        if (!res.ok) {
            const text = await res.text().catch(() => '');
            throw new Error(`${res.status} ${text}`);
        }
        const data = await res.json().catch(() => ({}));
        return data.data !== undefined ? data.data : data;
    }, [API_BASE_URL, apiHeaders]);

    /**
     * Builds the analytics query string. Bundles and assets are repeated
     * as {@code bundleIds=1&bundleIds=2} so Spring binds them into a list.
     */
    const queryString = useMemo(() => {
        const parts = [];
        if (filters.startDate) parts.push(`startDate=${encodeURIComponent(filters.startDate)}`);
        if (filters.endDate) parts.push(`endDate=${encodeURIComponent(filters.endDate)}`);
        if (filters.assetType) parts.push(`assetType=${encodeURIComponent(filters.assetType)}`);
        if (filters.iboNumber) parts.push(`iboNumber=${encodeURIComponent(filters.iboNumber)}`);
        (filters.bundleIds || []).forEach((id) => parts.push(`bundleIds=${encodeURIComponent(id)}`));
        (filters.assetIds || []).forEach((id) => parts.push(`assetIds=${encodeURIComponent(id)}`));
        return parts.length ? `?${parts.join('&')}` : '';
    }, [filters]);

    const withParam = (qs, extra) => `${qs}${qs ? '&' : '?'}${extra}`;

    const fetchOverview = useCallback(async () => {
        const data = await callApi(`/api/v1/content/admin/analytics/overview${queryString}`);
        setOverview(data);
    }, [callApi, queryString]);

    const fetchTopAssets = useCallback(async () => {
        const data = await callApi(
            withParam(`/api/v1/content/admin/analytics/top-assets${queryString}`, 'limit=50'),
        );
        setTopAssets(Array.isArray(data) ? data : []);
    }, [callApi, queryString]);

    const fetchTopBundles = useCallback(async () => {
        const data = await callApi(
            withParam(`/api/v1/content/admin/analytics/top-bundles${queryString}`, 'limit=20'),
        );
        setTopBundles(Array.isArray(data) ? data : []);
    }, [callApi, queryString]);

    const fetchRecentSessions = useCallback(async () => {
        const data = await callApi(
            withParam(`/api/v1/content/admin/analytics/recent-sessions${queryString}`, 'limit=50'),
        );
        setRecentSessions(Array.isArray(data) ? data : []);
    }, [callApi, queryString]);

    const fetchViewsByDeviceByDate = useCallback(async () => {
        const data = await callApi(
            `/api/v1/content/admin/analytics/views-by-device-by-date${queryString}`,
        );
        setViewsByDeviceByDate({
            devices: Array.isArray(data?.devices) ? data.devices : [],
            series: Array.isArray(data?.series) ? data.series : [],
        });
    }, [callApi, queryString]);

    const fetchEngagementFunnel = useCallback(async () => {
        const data = await callApi(
            `/api/v1/content/admin/analytics/engagement-funnel${queryString}`,
        );
        setEngagementFunnel(Array.isArray(data) ? data : []);
    }, [callApi, queryString]);

    const fetchTimeOfDayHeatmap = useCallback(async () => {
        const data = await callApi(
            `/api/v1/content/admin/analytics/time-of-day-heatmap${queryString}`,
        );
        setTimeOfDayHeatmap(Array.isArray(data?.cells) ? data.cells : []);
    }, [callApi, queryString]);

    const fetchBrowserOs = useCallback(async () => {
        const data = await callApi(
            `/api/v1/content/admin/analytics/browser-os${queryString}`,
        );
        setBrowserOs({ browser: data?.browser || {}, os: data?.os || {} });
    }, [callApi, queryString]);

    const refresh = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            await Promise.all([
                fetchOverview(),
                fetchTopAssets(),
                fetchTopBundles(),
                fetchRecentSessions(),
                fetchViewsByDeviceByDate(),
                fetchEngagementFunnel(),
                fetchTimeOfDayHeatmap(),
                fetchBrowserOs(),
            ]);
            setLastRefreshed(new Date());
        } catch (err) {
            setError(`Failed to load analytics: ${err.message}`);
        } finally {
            setLoading(false);
        }
    }, [fetchOverview, fetchTopAssets, fetchTopBundles, fetchRecentSessions,
        fetchViewsByDeviceByDate, fetchEngagementFunnel, fetchTimeOfDayHeatmap, fetchBrowserOs]);

    useEffect(() => { refresh(); }, [refresh]);

    // Filter option fetchers — fired once on mount; results don't change as
    // the admin tweaks date filters.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [bundles, assets] = await Promise.all([
                    callApi('/api/v1/content/bundles?activeOnly=true'),
                    callApi('/api/v1/content/admin/analytics/assets/lookup?limit=500'),
                ]);
                if (cancelled) return;
                setBundleOptions(Array.isArray(bundles)
                    ? bundles.map((b) => ({
                        id: b.id ?? b.bundleId ?? b.ID,
                        title: b.title ?? b.name ?? `Bundle ${b.id}`,
                    })).filter((b) => b.id != null)
                    : []);
                setAssetOptions(Array.isArray(assets)
                    ? assets.map((a) => ({
                        id: a.id,
                        title: a.title ?? a.id,
                        type: a.type ?? '',
                    }))
                    : []);
            } catch (err) {
                // Filters are optional UI; don't surface as a hard error.
                // eslint-disable-next-line no-console
                console.debug('[content-analytics] filter option load failed', err);
            }
        })();
        return () => { cancelled = true; };
    }, [callApi]);

    const openAssetDrilldown = async (asset) => {
        setSelectedAsset(asset);
        setLoadingAsset(true);
        setRetentionCurve([]);
        setDocumentPageStats([]);
        try {
            const type = (asset.assetType || '').toUpperCase();
            if (type === 'PDF' || type === 'WORD') {
                const pages = await callApi(
                    `/api/v1/content/admin/analytics/assets/${asset.assetId}/page-stats${queryString}`,
                );
                setDocumentPageStats(Array.isArray(pages) ? pages : []);
            } else {
                const retention = await callApi(
                    `/api/v1/content/admin/analytics/assets/${asset.assetId}/retention${queryString}`,
                );
                setRetentionCurve(retention?.curve || []);
            }
        } catch (err) {
            setError(`Failed to load asset detail: ${err.message}`);
        } finally {
            setLoadingAsset(false);
        }
    };

    const handleIboLookup = async () => {
        if (!iboSearch) return;
        setLoadingIbo(true);
        setIboStats(null);
        try {
            const data = await callApi(
                `/api/v1/content/admin/analytics/ibo/${encodeURIComponent(iboSearch)}${queryString}`,
            );
            setIboStats(data);
        } catch (err) {
            setError(`Failed to load IBO stats: ${err.message}`);
        } finally {
            setLoadingIbo(false);
        }
    };

    const handleFilter = (field, value) => setFilters((prev) => ({ ...prev, [field]: value }));

    /**
     * Day-aware duration formatter. Past one day the seconds slot is
     * dropped; past one hour the seconds are dropped. Keeps the display
     * short ("12d 14h", "3h 22m") while still being precise for short
     * sessions ("1m 5s", "42s").
     */
    const formatDuration = (seconds) => {
        if (!seconds || seconds < 0) return '0s';
        const s = Math.floor(seconds);
        const days = Math.floor(s / 86400);
        const hours = Math.floor((s % 86400) / 3600);
        const minutes = Math.floor((s % 3600) / 60);
        const remainder = s % 60;
        if (days > 0) return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
        if (hours > 0) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
        if (minutes > 0) return remainder > 0 ? `${minutes}m ${remainder}s` : `${minutes}m`;
        return `${remainder}s`;
    };
    const formatDate = (d) => (d ? new Date(d).toLocaleString() : '—');
    const formatDateShort = (d) => (d ? new Date(d).toLocaleDateString() : '—');
    const formatNumber = (n) => (n != null ? new Intl.NumberFormat('en-US').format(n) : '0');
    const formatPercent = (v) => (v != null ? `${Number(v).toFixed(1)}%` : '—');

    const assetTypeChartData = useMemo(() => {
        if (!overview?.viewsByAssetType) return [];
        return Object.entries(overview.viewsByAssetType).map(([name, value]) => ({ name, value }));
    }, [overview]);

    const viewsTrend = overview?.viewsTrend || [];

    const browserData = useMemo(() => (
        Object.entries(browserOs.browser || {})
            .sort(([, a], [, b]) => b - a)
            .slice(0, 6)
            .map(([name, value]) => ({ name, value }))
    ), [browserOs]);
    const osData = useMemo(() => (
        Object.entries(browserOs.os || {})
            .sort(([, a], [, b]) => b - a)
            .slice(0, 6)
            .map(([name, value]) => ({ name, value }))
    ), [browserOs]);

    const sortedTopAssets = useMemo(() => {
        const { key, dir } = topAssetsSort;
        const sorted = [...topAssets].sort((a, b) => {
            const av = a[key];
            const bv = b[key];
            if (av == null && bv == null) return 0;
            if (av == null) return 1;
            if (bv == null) return -1;
            if (typeof av === 'string') {
                return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
            }
            const an = Number(av);
            const bn = Number(bv);
            return dir === 'asc' ? an - bn : bn - an;
        });
        return sorted;
    }, [topAssets, topAssetsSort]);

    const toggleSort = (key) => setTopAssetsSort((prev) => (
        prev.key === key
            ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
            : { key, dir: 'desc' }
    ));

    const toDateInput = (iso) => (iso ? iso.slice(0, 10) : '');

    // Time-of-day heatmap — fold the rows into a 7x24 matrix so the cells
    // render in a deterministic grid even on sparse data. {@code dow}
    // matches MySQL's DAYOFWEEK (1 = Sunday, 7 = Saturday).
    const heatmapMatrix = useMemo(() => {
        const grid = Array.from({ length: 7 }, () => Array(24).fill(0));
        let max = 0;
        for (const cell of timeOfDayHeatmap) {
            const dow = (cell.dayOfWeek - 1 + 7) % 7;
            const hod = cell.hourOfDay;
            const events = Number(cell.events) || 0;
            if (dow >= 0 && dow < 7 && hod >= 0 && hod < 24) {
                grid[dow][hod] = events;
                if (events > max) max = events;
            }
        }
        return { grid, max };
    }, [timeOfDayHeatmap]);

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            {/* Header */}
            <Paper
                elevation={0}
                sx={{
                    p: 2.5,
                    mb: 2.5,
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    color: 'primary.contrastText',
                }}
            >
                <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={1.5}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Avatar sx={{ bgcolor: 'rgba(255,255,255,.18)' }}>
                            <AnalyticsIcon />
                        </Avatar>
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 700 }}>Content Analytics</Typography>
                            <Typography variant="body2" sx={{ opacity: 0.85 }}>
                                Engagement, retention &amp; reach across every asset
                            </Typography>
                        </Box>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        {lastRefreshed && (
                            <Chip
                                size="small"
                                label={`Updated ${lastRefreshed.toLocaleTimeString()}`}
                                sx={{ bgcolor: 'rgba(255,255,255,.18)', color: 'inherit', fontWeight: 500 }}
                            />
                        )}
                        <Button
                            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                            variant="contained"
                            color="secondary"
                            onClick={refresh}
                            disabled={loading}
                            sx={{ bgcolor: 'rgba(255,255,255,.2)', '&:hover': { bgcolor: 'rgba(255,255,255,.3)' } }}
                        >
                            Refresh
                        </Button>
                    </Stack>
                </Stack>
            </Paper>

            {/* Filters */}
            <Paper elevation={0} sx={{ p: 2, mb: 2.5, borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
                    <FilterAltIcon color="primary" fontSize="small" />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Filters</Typography>
                </Stack>
                <Grid container spacing={2} alignItems="center">
                    <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Start Date"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            value={toDateInput(filters.startDate)}
                            onChange={(e) => handleFilter('startDate', e.target.value ? new Date(e.target.value).toISOString() : null)}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label="End Date"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            value={toDateInput(filters.endDate)}
                            onChange={(e) => handleFilter('endDate', e.target.value ? new Date(e.target.value).toISOString() : null)}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 1.5 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Asset Type</InputLabel>
                            <Select
                                label="Asset Type"
                                value={filters.assetType}
                                onChange={(e) => handleFilter('assetType', e.target.value)}
                                MenuProps={{
                                    TransitionProps: { timeout: 0 },
                                    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                                    transformOrigin: { vertical: 'top', horizontal: 'left' },
                                }}
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="VIDEO">Video</MenuItem>
                                <MenuItem value="AUDIO">Audio</MenuItem>
                                <MenuItem value="PDF">PDF</MenuItem>
                                <MenuItem value="WORD">Word</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 2.5 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Bundles</InputLabel>
                            <Select
                                multiple
                                input={<OutlinedInput label="Bundles" />}
                                value={filters.bundleIds}
                                onChange={(e) => handleFilter('bundleIds', e.target.value)}
                                renderValue={(selected) => selected.length === 1
                                    ? (bundleOptions.find((b) => b.id === selected[0])?.title || selected[0])
                                    : `${selected.length} bundles`}
                                MenuProps={{
                                    TransitionProps: { timeout: 0 },
                                    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                                    transformOrigin: { vertical: 'top', horizontal: 'left' },
                                    PaperProps: { sx: { maxHeight: 380 } },
                                }}
                            >
                                {bundleOptions.length === 0 ? (
                                    <MenuItem disabled value="">No bundles available</MenuItem>
                                ) : bundleOptions.map((b) => (
                                    <MenuItem key={b.id} value={b.id}>
                                        <Checkbox size="small" checked={filters.bundleIds.indexOf(b.id) > -1} />
                                        <ListItemText primary={b.title} secondary={`#${b.id}`} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 2.5 }}>
                        <Autocomplete
                            multiple
                            size="small"
                            options={assetOptions}
                            value={assetOptions.filter((a) => filters.assetIds.includes(a.id))}
                            onChange={(_, value) => handleFilter('assetIds', value.map((v) => v.id))}
                            getOptionLabel={(option) => option.title || option.id}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            disableCloseOnSelect
                            limitTags={2}
                            renderInput={(params) => <TextField {...params} label="Assets" />}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 1.5 }}>
                        <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<ResetIcon />}
                            onClick={() => setFilters(buildDefaultFilters())}
                        >
                            Reset
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {loading && <LinearProgress sx={{ mb: 2, borderRadius: 2 }} />}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
            )}

            {/* Tabs */}
            <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, mb: 2.5 }}>
                <Tabs
                    value={activeTab}
                    onChange={(_, v) => setActiveTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ px: 1.5 }}
                >
                    <Tab value="overview" icon={<TrendingUpIcon />} iconPosition="start" label="Overview" />
                    <Tab value="assets" icon={<VideoIcon />} iconPosition="start" label="Top Assets" />
                    <Tab value="bundles" icon={<BundleIcon />} iconPosition="start" label="Top Bundles" />
                    <Tab value="sessions" icon={<VisibilityIcon />} iconPosition="start" label="Recent Sessions" />
                    <Tab value="ibo" icon={<PeopleIcon />} iconPosition="start" label="IBO Drilldown" />
                </Tabs>
            </Paper>

            {activeTab === 'overview' && (
                <>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 6, md: 3 }}>
                            <KpiCard icon={VisibilityIcon} label="Total Views" value={formatNumber(overview?.totalViews)} color="#1976d2" />
                        </Grid>
                        <Grid size={{ xs: 6, md: 3 }}>
                            <KpiCard
                                icon={PeopleIcon}
                                label="Unique Viewers"
                                value={formatNumber(overview?.uniqueViewers)}
                                color="#00bfa5"
                                helper="Authed + anonymous"
                            />
                        </Grid>
                        <Grid size={{ xs: 6, md: 3 }}>
                            <KpiCard icon={AccessTimeIcon} label="Watch Time" value={formatDuration(overview?.totalWatchTimeSeconds)} color="#8e44ad" />
                        </Grid>
                        <Grid size={{ xs: 6, md: 3 }}>
                            <KpiCard icon={SpeedIcon} label="Avg View Duration" value={formatDuration(overview?.avgViewDurationSeconds)} color="#ffb300" />
                        </Grid>
                        <Grid size={{ xs: 6, md: 3 }}>
                            <KpiCard
                                icon={CheckCircleOutlineIcon}
                                label="Completion Rate"
                                value={overview?.completionRate != null ? `${Number(overview.completionRate).toFixed(1)}%` : '—'}
                                color="#26a69a"
                            />
                        </Grid>
                        <Grid size={{ xs: 6, md: 3 }}>
                            <KpiCard icon={ThumbUpIcon} label="Likes" value={formatNumber(overview?.likes)} color="#ef5350" />
                        </Grid>
                        <Grid size={{ xs: 6, md: 3 }}>
                            <KpiCard icon={ShareIcon} label="Shares" value={formatNumber(overview?.shares)} color="#3949ab" />
                        </Grid>
                        <Grid size={{ xs: 6, md: 3 }}>
                            <KpiCard icon={DownloadIcon} label="Downloads" value={formatNumber(overview?.downloads)} color="#00838f" />
                        </Grid>
                    </Grid>

                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid size={{ xs: 12, md: 7 }}>
                            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                                <SectionHeader icon={TrendingUpIcon} title="Views Trend" />
                                {viewsTrend.length === 0 ? (
                                    <EmptyState />
                                ) : (
                                    <ResponsiveContainer width="100%" height={280}>
                                        <AreaChart data={viewsTrend}>
                                            <defs>
                                                <linearGradient id="gViews" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#1976d2" stopOpacity={0.5} />
                                                    <stop offset="95%" stopColor="#1976d2" stopOpacity={0.02} />
                                                </linearGradient>
                                                <linearGradient id="gUnique" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#00bfa5" stopOpacity={0.5} />
                                                    <stop offset="95%" stopColor="#00bfa5" stopOpacity={0.02} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                                            <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                                            <YAxis tick={{ fontSize: 12 }} />
                                            <RTooltip />
                                            <Legend />
                                            <Area type="monotone" dataKey="views" stroke="#1976d2" fill="url(#gViews)" name="Views" strokeWidth={2} />
                                            <Area type="monotone" dataKey="uniqueViewers" stroke="#00bfa5" fill="url(#gUnique)" name="Unique" strokeWidth={2} />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                )}
                            </Paper>
                        </Grid>
                        <Grid size={{ xs: 12, md: 5 }}>
                            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                                <SectionHeader icon={InsightsIcon} title="Engagement Funnel" />
                                {engagementFunnel.length === 0 ? (
                                    <EmptyState message="No sessions yet" />
                                ) : (
                                    <ResponsiveContainer width="100%" height={280}>
                                        <FunnelChart>
                                            <RTooltip />
                                            <Funnel
                                                dataKey="count"
                                                data={engagementFunnel}
                                                isAnimationActive={false}
                                            >
                                                {engagementFunnel.map((_, i) => (
                                                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                                                ))}
                                                <LabelList position="right" dataKey="label" stroke="none" fill="#222" />
                                                <LabelList position="left" dataKey="count" stroke="none" fill="#444" formatter={(v) => formatNumber(v)} />
                                            </Funnel>
                                        </FunnelChart>
                                    </ResponsiveContainer>
                                )}
                            </Paper>
                        </Grid>
                    </Grid>

                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid size={{ xs: 12, md: 7 }}>
                            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                                <SectionHeader icon={DevicesIcon} title="Views by Device by Date" />
                                {viewsByDeviceByDate.series.length === 0 ? (
                                    <EmptyState />
                                ) : (
                                    <ResponsiveContainer width="100%" height={280}>
                                        <BarChart data={viewsByDeviceByDate.series}>
                                            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                                            <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                                            <YAxis tick={{ fontSize: 12 }} />
                                            <RTooltip />
                                            <Legend />
                                            {viewsByDeviceByDate.devices.map((device, i) => (
                                                <Bar
                                                    key={device}
                                                    dataKey={device}
                                                    stackId="views"
                                                    fill={DEVICE_COLORS[device] || CHART_COLORS[i % CHART_COLORS.length]}
                                                    name={device.charAt(0).toUpperCase() + device.slice(1)}
                                                />
                                            ))}
                                        </BarChart>
                                    </ResponsiveContainer>
                                )}
                            </Paper>
                        </Grid>
                        <Grid size={{ xs: 12, md: 5 }}>
                            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                                <SectionHeader icon={InsightsIcon} title="Views by Asset Type" />
                                {assetTypeChartData.length === 0 ? (
                                    <EmptyState />
                                ) : (
                                    <ResponsiveContainer width="100%" height={280}>
                                        <PieChart>
                                            <Pie
                                                data={assetTypeChartData}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={55}
                                                outerRadius={95}
                                                paddingAngle={3}
                                                dataKey="value"
                                                label={(e) => `${e.name}: ${formatNumber(e.value)}`}
                                            >
                                                {assetTypeChartData.map((_, i) => (
                                                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <RTooltip />
                                        </PieChart>
                                    </ResponsiveContainer>
                                )}
                            </Paper>
                        </Grid>
                    </Grid>

                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid size={{ xs: 12, md: 7 }}>
                            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                                <SectionHeader icon={AccessTimeIcon} title="Time of Day Heatmap" />
                                {heatmapMatrix.max === 0 ? (
                                    <EmptyState message="No activity yet" />
                                ) : (
                                    <Box sx={{ overflowX: 'auto' }}>
                                        <Box sx={{
                                            display: 'grid',
                                            gridTemplateColumns: 'auto repeat(24, 1fr)',
                                            gap: 0.5,
                                            minWidth: 640,
                                        }}>
                                            <Box />
                                            {Array.from({ length: 24 }).map((_, h) => (
                                                <Typography key={h} variant="caption" align="center" sx={{ color: 'text.secondary' }}>
                                                    {h % 3 === 0 ? `${h}h` : ''}
                                                </Typography>
                                            ))}
                                            {DAY_LABELS.map((label, dow) => (
                                                <React.Fragment key={label}>
                                                    <Typography variant="caption" sx={{ pr: 1, color: 'text.secondary', alignSelf: 'center' }}>
                                                        {label}
                                                    </Typography>
                                                    {heatmapMatrix.grid[dow].map((events, hod) => {
                                                        const intensity = heatmapMatrix.max > 0 ? events / heatmapMatrix.max : 0;
                                                        return (
                                                            <Tooltip
                                                                key={hod}
                                                                title={`${label} ${hod}:00 — ${formatNumber(events)} events`}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        height: 22,
                                                                        borderRadius: 0.5,
                                                                        backgroundColor: events === 0
                                                                            ? 'action.hover'
                                                                            : `rgba(25, 118, 210, ${Math.max(0.12, intensity)})`,
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                        );
                                                    })}
                                                </React.Fragment>
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                            </Paper>
                        </Grid>
                        <Grid size={{ xs: 12, md: 5 }}>
                            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <SectionHeader icon={WebIcon} title="Browser" />
                                        {browserData.length === 0 ? (
                                            <EmptyState message="No data" />
                                        ) : (
                                            <ResponsiveContainer width="100%" height={200}>
                                                <PieChart>
                                                    <Pie data={browserData} dataKey="value" innerRadius={42} outerRadius={70} paddingAngle={2}>
                                                        {browserData.map((_, i) => (
                                                            <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                                                        ))}
                                                    </Pie>
                                                    <Legend wrapperStyle={{ fontSize: 11 }} />
                                                    <RTooltip />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        )}
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <SectionHeader icon={DeviceHubIcon} title="OS" />
                                        {osData.length === 0 ? (
                                            <EmptyState message="No data" />
                                        ) : (
                                            <ResponsiveContainer width="100%" height={200}>
                                                <PieChart>
                                                    <Pie data={osData} dataKey="value" innerRadius={42} outerRadius={70} paddingAngle={2}>
                                                        {osData.map((_, i) => (
                                                            <Cell key={i} fill={CHART_COLORS[(i + 3) % CHART_COLORS.length]} />
                                                        ))}
                                                    </Pie>
                                                    <Legend wrapperStyle={{ fontSize: 11 }} />
                                                    <RTooltip />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        )}
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Grid>
                    </Grid>

                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid size={{ xs: 12 }}>
                            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
                                <SectionHeader icon={PublicIcon} title="Top Geographies" />
                                {!overview?.topCountries || overview.topCountries.length === 0 ? (
                                    <EmptyState message="No geo data yet" />
                                ) : (
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 700 }}>Country</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 700 }}>Views</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {overview.topCountries.map((c, i) => (
                                                    <TableRow key={i} hover>
                                                        <TableCell>
                                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                                <FlagIcon fontSize="small" color="action" />
                                                                <Typography variant="body2">{c.country}</Typography>
                                                            </Stack>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Chip size="small" label={formatNumber(c.views)} color="primary" variant="outlined" />
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </Paper>
                        </Grid>
                    </Grid>
                </>
            )}

            {activeTab === 'assets' && (
                <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, p: 0, overflow: 'hidden' }}>
                    <Box sx={{ p: 2.5, pb: 1 }}>
                        <SectionHeader icon={VideoIcon} title="Top Assets" />
                    </Box>
                    <TableContainer>
                        <Table>
                            <TableHead sx={{ bgcolor: 'action.hover' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700 }}>
                                        <TableSortLabel
                                            active={topAssetsSort.key === 'title'}
                                            direction={topAssetsSort.dir}
                                            onClick={() => toggleSort('title')}
                                        >
                                            Asset
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Bundle</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                                        <TableSortLabel
                                            active={topAssetsSort.key === 'totalViews'}
                                            direction={topAssetsSort.dir}
                                            onClick={() => toggleSort('totalViews')}
                                        >
                                            Views
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                                        <TableSortLabel
                                            active={topAssetsSort.key === 'uniqueViewers'}
                                            direction={topAssetsSort.dir}
                                            onClick={() => toggleSort('uniqueViewers')}
                                        >
                                            Unique
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                                        <TableSortLabel
                                            active={topAssetsSort.key === 'reachPercent'}
                                            direction={topAssetsSort.dir}
                                            onClick={() => toggleSort('reachPercent')}
                                        >
                                            Reach
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                                        <TableSortLabel
                                            active={topAssetsSort.key === 'totalWatchTimeSeconds'}
                                            direction={topAssetsSort.dir}
                                            onClick={() => toggleSort('totalWatchTimeSeconds')}
                                        >
                                            Watch Time
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                                        <TableSortLabel
                                            active={topAssetsSort.key === 'avgWatchTimeSecondsPerSession'}
                                            direction={topAssetsSort.dir}
                                            onClick={() => toggleSort('avgWatchTimeSecondsPerSession')}
                                        >
                                            Avg / Session
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                                        <TableSortLabel
                                            active={topAssetsSort.key === 'avgCompletionPercent'}
                                            direction={topAssetsSort.dir}
                                            onClick={() => toggleSort('avgCompletionPercent')}
                                        >
                                            Completion
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                                        <TableSortLabel
                                            active={topAssetsSort.key === 'lastWatchedAt'}
                                            direction={topAssetsSort.dir}
                                            onClick={() => toggleSort('lastWatchedAt')}
                                        >
                                            Last Watched
                                        </TableSortLabel>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sortedTopAssets.map((a) => {
                                    const meta = assetMeta(a.assetType);
                                    const Icon = meta.icon;
                                    const completionNum = a.avgCompletionPercent != null ? Number(a.avgCompletionPercent) : null;
                                    const reachNum = a.reachPercent != null ? Number(a.reachPercent) : null;
                                    return (
                                        <TableRow
                                            key={a.assetId}
                                            hover
                                            onClick={() => openAssetDrilldown(a)}
                                            sx={{ cursor: 'pointer' }}
                                        >
                                            <TableCell>
                                                <Stack direction="row" alignItems="center" spacing={1.2}>
                                                    <Avatar sx={{ bgcolor: `${meta.color}22`, color: meta.color, width: 32, height: 32 }}>
                                                        <Icon fontSize="small" />
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                            {a.title || a.assetId}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                                            {a.assetId}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            </TableCell>
                                            <TableCell>
                                                {a.bundleTitle ? (
                                                    <Chip size="small" icon={<BundleIcon fontSize="small" />} label={a.bundleTitle} variant="outlined" />
                                                ) : (
                                                    <Typography variant="caption" color="text.disabled">—</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell align="right">{formatNumber(a.totalViews)}</TableCell>
                                            <TableCell align="right">{formatNumber(a.uniqueViewers)}</TableCell>
                                            <TableCell align="right">{reachNum != null ? `${reachNum.toFixed(1)}%` : '—'}</TableCell>
                                            <TableCell align="right">{formatDuration(a.totalWatchTimeSeconds)}</TableCell>
                                            <TableCell align="right">{formatDuration(a.avgWatchTimeSecondsPerSession)}</TableCell>
                                            <TableCell align="right">
                                                {completionNum != null ? (
                                                    <Chip
                                                        size="small"
                                                        label={`${completionNum.toFixed(1)}%`}
                                                        color={completionNum >= 70 ? 'success' : completionNum >= 40 ? 'warning' : 'default'}
                                                        variant={completionNum >= 40 ? 'filled' : 'outlined'}
                                                    />
                                                ) : '—'}
                                            </TableCell>
                                            <TableCell align="right">{formatDateShort(a.lastWatchedAt)}</TableCell>
                                        </TableRow>
                                    );
                                })}
                                {sortedTopAssets.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={9}>
                                            <EmptyState
                                                icon={InboxIcon}
                                                message="No assets in this window"
                                                hint="Widen the date range or clear bundle / asset filters."
                                            />
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            {activeTab === 'bundles' && (
                <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
                    <Box sx={{ p: 2.5, pb: 1 }}>
                        <SectionHeader icon={BundleIcon} title="Top Bundles" />
                    </Box>
                    {topBundles.length === 0 ? (
                        <Box sx={{ p: 3 }}>
                            <EmptyState
                                icon={InboxIcon}
                                message="No bundle activity yet"
                                hint="Bundle-level rollup needs at least one play started while the asset's bundle was set."
                            />
                        </Box>
                    ) : (
                        <>
                            <Box sx={{ p: 2.5, pt: 0 }}>
                                <ResponsiveContainer width="100%" height={260}>
                                    <BarChart data={topBundles} layout="vertical" margin={{ left: 20, right: 30 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                                        <XAxis type="number" tick={{ fontSize: 12 }} />
                                        <YAxis type="category" dataKey="title" width={180} tick={{ fontSize: 12 }} />
                                        <RTooltip formatter={(value, name) => name === 'totalWatchTimeSeconds' ? formatDuration(value) : formatNumber(value)} />
                                        <Bar dataKey="totalWatchTimeSeconds" fill="#1976d2" name="Watch Time" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </Box>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead sx={{ bgcolor: 'action.hover' }}>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 700 }}>Bundle</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>Views</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>Unique</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>Watch Time</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {topBundles.map((b) => (
                                            <TableRow key={b.bundleId} hover>
                                                <TableCell>
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <BundleIcon fontSize="small" color="primary" />
                                                        <Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{b.title}</Typography>
                                                            <Typography variant="caption" color="text.secondary">#{b.bundleId}</Typography>
                                                        </Box>
                                                    </Stack>
                                                </TableCell>
                                                <TableCell align="right">{formatNumber(b.totalViews)}</TableCell>
                                                <TableCell align="right">{formatNumber(b.uniqueViewers)}</TableCell>
                                                <TableCell align="right">{formatDuration(b.totalWatchTimeSeconds)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}
                </Paper>
            )}

            {activeTab === 'sessions' && (
                <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
                    <Box sx={{ p: 2.5, pb: 1 }}>
                        <SectionHeader icon={VisibilityIcon} title="Recent Playback Sessions" />
                    </Box>
                    <TableContainer>
                        <Table size="small">
                            <TableHead sx={{ bgcolor: 'action.hover' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700 }}>Viewer</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Asset</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Bundle</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Started</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>Watch Time</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>Reached</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Device</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Browser</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>OS</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Country</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {recentSessions.map((s) => {
                                    const reached = s.reachedPercent != null ? Number(s.reachedPercent) : null;
                                    const isAuthed = s.viewerKind === 'AUTHED';
                                    return (
                                        <TableRow key={s.sessionId || s.id} hover>
                                            <TableCell>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Chip
                                                        size="small"
                                                        label={isAuthed ? 'Authed' : 'Anon'}
                                                        color={isAuthed ? 'primary' : 'default'}
                                                        variant={isAuthed ? 'filled' : 'outlined'}
                                                    />
                                                    <Tooltip title={s.iboNumber || s.anonymousId || s.sessionId || ''}>
                                                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                                                            {s.viewerLabel || (s.iboNumber ?? s.anonymousIdShort ?? (s.sessionId || '').slice(0, 8))}
                                                        </Typography>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title={s.assetId || ''}>
                                                    <Typography variant="body2" sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {s.assetTitle || s.assetId || '—'}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                {s.bundleTitle
                                                    ? <Chip size="small" label={s.bundleTitle} variant="outlined" />
                                                    : <Typography variant="caption" color="text.disabled">—</Typography>}
                                            </TableCell>
                                            <TableCell>{formatDate(s.startedAt)}</TableCell>
                                            <TableCell align="right">{formatDuration(Math.round((s.totalWatchTimeMs || 0) / 1000))}</TableCell>
                                            <TableCell align="right">{reached != null ? `${reached.toFixed(0)}%` : '—'}</TableCell>
                                            <TableCell>{s.deviceType || '—'}</TableCell>
                                            <TableCell>{s.deviceBrowser || '—'}</TableCell>
                                            <TableCell>{s.deviceOs || '—'}</TableCell>
                                            <TableCell>{s.geoCountry || '—'}</TableCell>
                                        </TableRow>
                                    );
                                })}
                                {recentSessions.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={10}>
                                            <EmptyState
                                                icon={InboxIcon}
                                                message="No sessions in this window"
                                                hint="Try widening the date filter or clearing bundle / asset filters. New playbacks usually appear within a minute."
                                            />
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            {activeTab === 'ibo' && (
                <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
                    <SectionHeader icon={PeopleIcon} title="Per-IBO / Anonymous Drilldown" />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Search by IBO number or by anonymous short hash (first 8 chars of the {`anonymousId`}).
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
                        <TextField
                            size="small"
                            label="IBO Number or Anonymous Hash"
                            value={iboSearch}
                            onChange={(e) => setIboSearch(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleIboLookup(); }}
                            sx={{ minWidth: 320 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Button
                            variant="contained"
                            onClick={handleIboLookup}
                            disabled={loadingIbo || !iboSearch}
                            startIcon={loadingIbo ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                        >
                            Lookup
                        </Button>
                    </Stack>

                    {iboStats ? (
                        <>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 6, md: 4 }}><KpiCard icon={VideoIcon} label="Assets Viewed" value={formatNumber(iboStats.assetsViewed)} color="#1976d2" /></Grid>
                                <Grid size={{ xs: 6, md: 4 }}><KpiCard icon={VisibilityIcon} label="Total Views" value={formatNumber(iboStats.totalViews)} color="#00bfa5" /></Grid>
                                <Grid size={{ xs: 6, md: 4 }}><KpiCard icon={AccessTimeIcon} label="Watch Time" value={formatDuration(iboStats.totalWatchTimeSeconds)} color="#8e44ad" /></Grid>
                                <Grid size={{ xs: 6, md: 4 }}><KpiCard icon={VisibilityIcon} label="Sessions" value={formatNumber(iboStats.sessions)} color="#3949ab" /></Grid>
                                <Grid size={{ xs: 6, md: 4 }}><KpiCard icon={ThumbUpIcon} label="Purchases" value={formatNumber(iboStats.purchases)} color="#ef5350" /></Grid>
                                <Grid size={{ xs: 6, md: 4 }}><KpiCard icon={CheckCircleOutlineIcon} label="Active Subs" value={formatNumber(iboStats.activeSubscriptions)} color="#26a69a" /></Grid>
                            </Grid>

                            {iboStats.recentAssets?.length > 0 && (
                                <Box sx={{ mt: 2.5 }}>
                                    <SectionHeader icon={PlayIcon} title="Recent Assets" />
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 700 }}>Asset</TableCell>
                                                    <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                                                    <TableCell sx={{ fontWeight: 700 }}>Last Watched</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 700 }}>Watch Time</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 700 }}>Reached</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {iboStats.recentAssets.map((a, i) => {
                                                    const meta = assetMeta(a.assetType);
                                                    const Icon = meta.icon;
                                                    return (
                                                        <TableRow key={i} hover>
                                                            <TableCell>
                                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                                    <Avatar sx={{ bgcolor: `${meta.color}22`, color: meta.color, width: 28, height: 28 }}>
                                                                        <Icon fontSize="inherit" />
                                                                    </Avatar>
                                                                    <Typography variant="body2">{a.title || a.assetId}</Typography>
                                                                </Stack>
                                                            </TableCell>
                                                            <TableCell>{a.assetType || '—'}</TableCell>
                                                            <TableCell>{formatDate(a.lastWatchedAt)}</TableCell>
                                                            <TableCell align="right">{formatDuration(Math.round((a.watchTimeMs || 0) / 1000))}</TableCell>
                                                            <TableCell align="right">{a.reachedPercent != null ? `${Number(a.reachedPercent).toFixed(0)}%` : '—'}</TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            )}

                            {iboStats.sessions === 0 && (
                                <EmptyState
                                    icon={InboxIcon}
                                    message={`No sessions found for ${iboSearch} in this range`}
                                    hint="Widen the date filter or confirm the user actually logged in while watching."
                                />
                            )}
                        </>
                    ) : (
                        <EmptyState
                            icon={InboxIcon}
                            message="Search to drill down"
                            hint="Enter an IBO number or the short hash from the Recent Sessions tab."
                        />
                    )}
                </Paper>
            )}

            {/* Asset drill-down drawer */}
            <Drawer
                anchor="right"
                open={!!selectedAsset}
                onClose={() => { setSelectedAsset(null); setRetentionCurve([]); setDocumentPageStats([]); }}
                PaperProps={{ sx: { width: { xs: '100%', md: 720 }, p: 0 } }}
            >
                {selectedAsset && (
                    <Box sx={{ p: 2.5 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                            <Stack direction="row" alignItems="center" spacing={1.2}>
                                {(() => {
                                    const meta = assetMeta(selectedAsset.assetType);
                                    const Icon = meta.icon;
                                    return (
                                        <Avatar sx={{ bgcolor: `${meta.color}22`, color: meta.color }}>
                                            <Icon />
                                        </Avatar>
                                    );
                                })()}
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                        {selectedAsset.title || selectedAsset.assetId}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                        {selectedAsset.assetId}
                                    </Typography>
                                </Box>
                            </Stack>
                            <IconButton onClick={() => { setSelectedAsset(null); setRetentionCurve([]); setDocumentPageStats([]); }}>
                                <CloseIcon />
                            </IconButton>
                        </Stack>

                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid size={{ xs: 6 }}><KpiCard icon={VisibilityIcon} label="Views" value={formatNumber(selectedAsset.totalViews)} color="#1976d2" /></Grid>
                            <Grid size={{ xs: 6 }}><KpiCard icon={PeopleIcon} label="Unique" value={formatNumber(selectedAsset.uniqueViewers)} color="#00bfa5" /></Grid>
                            <Grid size={{ xs: 6 }}><KpiCard icon={AccessTimeIcon} label="Watch Time" value={formatDuration(selectedAsset.totalWatchTimeSeconds)} color="#8e44ad" /></Grid>
                            <Grid size={{ xs: 6 }}>
                                <KpiCard
                                    icon={CheckCircleOutlineIcon}
                                    label="Completion"
                                    value={formatPercent(selectedAsset.avgCompletionPercent)}
                                    color="#26a69a"
                                />
                            </Grid>
                        </Grid>

                        {loadingAsset ? (
                            <Stack alignItems="center" sx={{ py: 4 }}>
                                <CircularProgress />
                            </Stack>
                        ) : (
                            <>
                                {retentionCurve.length > 0 && (
                                    <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, mb: 2 }}>
                                        <SectionHeader icon={TrendingUpIcon} title="Retention Curve" />
                                        <ResponsiveContainer width="100%" height={260}>
                                            <AreaChart data={retentionCurve}>
                                                <defs>
                                                    <linearGradient id="gRetention" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#1976d2" stopOpacity={0.5} />
                                                        <stop offset="95%" stopColor="#1976d2" stopOpacity={0.02} />
                                                    </linearGradient>
                                                </defs>
                                                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                                                <XAxis dataKey="startSeconds" label={{ value: 'Seconds', position: 'insideBottom', offset: -5 }} />
                                                <YAxis label={{ value: 'Viewers', angle: -90, position: 'insideLeft' }} />
                                                <RTooltip />
                                                <Area type="monotone" dataKey="views" stroke="#1976d2" fill="url(#gRetention)" strokeWidth={2} />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    </Paper>
                                )}
                                {documentPageStats.length > 0 && (
                                    <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                                        <SectionHeader icon={PdfIcon} title="Per-Page Stats" />
                                        <TableContainer>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Page</TableCell>
                                                        <TableCell align="right">Views</TableCell>
                                                        <TableCell align="right">Avg Time</TableCell>
                                                        <TableCell align="right">Exits</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {documentPageStats.map((p) => (
                                                        <TableRow key={p.pageNumber} hover>
                                                            <TableCell>{p.pageNumber}</TableCell>
                                                            <TableCell align="right">{formatNumber(p.views)}</TableCell>
                                                            <TableCell align="right">{formatDuration(Math.round((p.avgTimeMs || 0) / 1000))}</TableCell>
                                                            <TableCell align="right">{formatNumber(p.exits)}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Paper>
                                )}
                                {retentionCurve.length === 0 && documentPageStats.length === 0 && (
                                    <EmptyState message="No detail data available for this asset yet" />
                                )}
                            </>
                        )}
                    </Box>
                )}
            </Drawer>
        </Container>
    );
};

export default ContentAnalyticsPage;
